import { IMassaTotal } from "./AppEditData.types";

/**
 * Data "só dia" vinda do Snowflake (TIMESTAMP_NTZ serializado como meia-noite
 * UTC) lida como dia local: `new Date(iso)` mostrava o dia anterior no fuso de
 * Brasília. Usada na "Data Demissão", que para PJ é o fim do vínculo.
 */
export function parseDateOnly(value: unknown): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

/** Monta o corpo do POST de Editar dados a partir das mudanças da grid. */
export function buildEditDataPayload(changes: Array<{ [key: string]: any }>, people: Pick<IMassaTotal, "TAX_PAYER_ID" | "ORIGIN">[]) {
  return changes.map(change => {
    const result: any = {
      taxPayerId: change?.taxPayerId?.replace(/\D/g, ""),
      PERSON_TYPE: change?.type === "employee" ? "employee" : "dependent",
      ORIGIN: people.find(p => p.TAX_PAYER_ID === change?.taxPayerId?.replace(/\D/g, ""))?.ORIGIN,
      name: typeof change?.name === 'string' ? change.name : change?.name?.newer,
      employeeTaxPayerId: change?.taxPayerIdEmployee?.replace?.(/\D/g, "") || undefined,
    };

    const getValue = (value: any) => value === null ? undefined : value;

    // Common fields
    // if (change?.name?.newer !== undefined) result.name = getValue(change.name.newer);
    if (change?.taxPayerIdEmployee?.newer !== undefined) result.employeeTaxPayerId = change.taxPayerIdEmployee.newer?.replace(/\D/g, "") || undefined;
    if (change?.birthDate?.newer !== undefined) result.birthDate = getValue(change.birthDate.newer);
    if (change?.motherName?.newer !== undefined) result.motherName = getValue(change.motherName.newer);
    if (change?.weddingDate?.newer !== undefined) result.weddingDate = getValue(change.weddingDate.newer);

    // Employee specific fields  
    if (change?.organizationId?.newer !== undefined) result.organizationId = getValue(change.organizationId.newer);
    if (change?.cnpj?.newer !== undefined) result.cnpj = getValue(change.cnpj.newer);
    if (change?.pis?.newer !== undefined) result.pis = getValue(change.pis.newer);
    if (change?.genderId?.newer !== undefined) result.genderId = getValue(change.genderId.newer);
    if (change?.raceId?.newer !== undefined) result.raceId = getValue(change.raceId.newer);
    if (change?.maritalStatusId?.newer !== undefined) result.maritalStatusId = getValue(change.maritalStatusId.newer);
    if (change?.educationLevelId?.newer !== undefined) result.educationLevelId = getValue(change.educationLevelId.newer);
    if (change?.socialName?.newer !== undefined) result.socialName = getValue(change.socialName.newer);
    if (change?.birthCountryId?.newer !== undefined) result.birthCountryId = getValue(change.birthCountryId.newer);
    if (change?.nationalityCountryId?.newer !== undefined) result.nationalityCountryId = getValue(change.nationalityCountryId.newer);
    if (change?.birthStateId?.newer !== undefined) result.birthStateId = getValue(change.birthStateId.newer);
    if (change?.birthCityId?.newer !== undefined) result.birthCityId = getValue(change.birthCityId.newer);
    if (change?.fatherName?.newer !== undefined) result.fatherName = getValue(change.fatherName.newer);
    if (change?.zipCode?.newer !== undefined) result.zipCode = getValue(change.zipCode.newer);
    if (change?.addressType?.newer !== undefined) result.addressType = getValue(change.addressType.newer);
    if (change?.address?.newer !== undefined) result.address = getValue(change.address.newer);
    if (change?.addressNumber?.newer !== undefined) result.addressNumber = getValue(change.addressNumber.newer);
    if (change?.complement?.newer !== undefined) result.complement = getValue(change.complement.newer);
    if (change?.neighborhood?.newer !== undefined) result.neighborhood = getValue(change.neighborhood.newer);
    if (change?.stateId?.newer !== undefined) result.stateId = getValue(change.stateId.newer);
    if (change?.cityId?.newer !== undefined) result.cityId = getValue(change.cityId.newer);
    if (change?.nationalIdentityNumber?.newer !== undefined) result.nationalIdentityNumber = getValue(change.nationalIdentityNumber.newer);
    if (change?.nationalIdentityEmissor?.newer !== undefined) result.nationalIdentityEmissor = getValue(change.nationalIdentityEmissor.newer);
    if (change?.nationalIdentityExpeditionDate?.newer !== undefined) result.nationalIdentityExpeditionDate = getValue(change.nationalIdentityExpeditionDate.newer);
    if (change?.phoneNumber?.newer !== undefined) result.phoneNumber = getValue(change.phoneNumber.newer);
    if (change?.email?.newer !== undefined) result.email = getValue(change.email.newer);
    if (change?.hiringDate?.newer !== undefined) result.hiringDate = getValue(change.hiringDate.newer);

    // New fields from IMassaTotal
    if (change?.status?.newer !== undefined) result.status = getValue(change.status.newer);
    if (change?.jobTitle?.newer !== undefined) result.jobTitle = getValue(change.jobTitle.newer);
    if (change?.paymentFrequency?.newer !== undefined) result.paymentFrequency = getValue(change.paymentFrequency.newer);
    if (change?.grossPayment?.newer !== undefined) result.grossPayment = getValue(change.grossPayment.newer);
    if (change?.municipality?.newer !== undefined) result.municipality = getValue(change.municipality.newer);
    if (change?.cbo?.newer !== undefined) result.cbo = getValue(change.cbo.newer);
    // Para PJ, "Data Demissão" grava o fim do vínculo. Data apagada vai como
    // null (getValue a tiraria do JSON) e o backend grava FIM_CANCELADO.
    if (change?.dismissalDate?.newer !== undefined) result.dismissalDate = change.dismissalDate.newer;
    if (change?.transferred?.newer !== undefined) result.transferred = getValue(change.transferred.newer);
    if (change?.plan?.newer !== undefined) result.plan = getValue(change.plan.newer);

    // Dependent specific fields
    if (change?.employeeId?.newer !== undefined) result.employeeId = getValue(change.employeeId.newer);
    if (change?.dependentTypeId?.newer !== undefined) result.dependentTypeId = getValue(change.dependentTypeId.newer);
    if (change?.isStudent?.newer !== undefined) {
      result.isStudent = change.isStudent.newer === 'Sim' ? true : change.isStudent.newer === 'Não' ? false : undefined;
    }

    return result;
  });
}

/** Acrescenta a cada item a partição (dagRunId e movementType) do seu CPF, quando houver. */
export function aplicarParticao<T extends { taxPayerId?: string }>(
  payload: T[],
  partição?: Record<string, { dagRunId: string; movementType?: string }>,
) {
  if (!partição) return payload;
  return payload.map(item => ({ ...item, ...(item.taxPayerId ? partição[item.taxPayerId] : undefined) }));
}
